import { FC, useState } from 'react';
import {
  Box,
  Button,
  HStack,
  Heading,
  Image,
  Table,
  TableContainer,
  Tbody,
  Td,
  Thead,
  Tr,
  InputGroup,
  InputLeftElement,
  Input,
  useDisclosure,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  VStack,
} from '@chakra-ui/react';
import { useDebounce } from 'usehooks-ts';

import { ReactComponent as DotsIcon } from '../../../assets/icons/dots.svg';
import { ReactComponent as FiltersIcon } from '../../../assets/icons/filters.svg';
import { ReactComponent as SearchIcon } from '../../../assets/icons/search.svg';
import InviteIcon from '../../../assets/projects/invite.svg';
import {
  useGetTeamMembersByPhaseIdQuery,
  useUnAssignTeamMemberToPhaseMutation,
} from '../../../services';
import {
  FilterDrawer,
  CenteredSpinner,
  RoundedBadge,
  Paginator,
  TableHeader,
  UserData,
} from '../../../components';
import { userFilters } from '../../../utils';
import { CompanyStatus } from '../../../models';
import { AssignTeamMemberModal } from '.';
import { useParams } from 'react-router-dom';

export const TeamList: FC<{ phaseId: string }> = ({ phaseId }) => {
  const [searchTerm, setSearchTerm] = useState<string>();
  const [page, setPage] = useState(0);
  const [filters, setFilters] = useState<{ status?: CompanyStatus }>({
    status: undefined,
  });
  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const {
    isOpen: isAssignTeamMemberModalOpen,
    onOpen: onOpenAssignTeamMemberModal,
    onClose: onCloseAssignTeamMemberModal,
  } = useDisclosure();
  const { projectId } = useParams();

  const { data, isLoading, isFetching, refetch } =
    useGetTeamMembersByPhaseIdQuery({
      projectId: String(projectId),
      phaseId,
      page,
      name: String(debouncedSearchTerm),
    });

  const [unAssignTeamMemberToPhase] = useUnAssignTeamMemberToPhaseMutation();

  const handleFilterDrawerClose = (filterValues?: {
    status: CompanyStatus;
  }) => {
    onClose();
    if (!filterValues) return;

    setFilters({ status: filterValues.status });

    void refetch();
  };

  const handleRemoveFilter = (filterKey: keyof typeof filters) => {
    setFilters({ ...filters, [filterKey]: undefined });

    void refetch();
  };

  return (
    <Box h="full">
      {isOpen && (
        <FilterDrawer
          isOpen={isOpen}
          onClose={handleFilterDrawerClose}
          filters={userFilters}
        />
      )}

      {isAssignTeamMemberModalOpen && (
        <AssignTeamMemberModal
          isOpen={isAssignTeamMemberModalOpen}
          onClose={onCloseAssignTeamMemberModal}
          phaseId={phaseId}
        />
      )}

      <Box display="flex" flexDirection="column" flex="1" h="full">
        <HStack justify="space-between" mt="4" align="start">
          <Heading color="custom_blue.1" fontSize="2xl">
            Team
          </Heading>

          <HStack>
            {/* TODO: reuse after implementation */}
            {/* <Button
              bg="transparent"
              _hover={{ bg: 'transparent' }}
              color="custom_blue.1"
              fontWeight="semibold"
              fontSize="sm"
              variant="outline"
              borderColor="custom_gray.1"
            >
              <Image src={PlusIcon} alt="plus icon" boxSize="4" mr="1" />
              Add
            </Button> */}

            <Button
              bg="custom_orange.2"
              _hover={{ bg: 'custom_orange.2' }}
              color="white"
              fontWeight="semibold"
              fontSize="sm"
              onClick={onOpenAssignTeamMemberModal}
            >
              <Image src={InviteIcon} alt="plus icon" boxSize="5" mr="1" />
              Invite a Team Member
            </Button>
          </HStack>
        </HStack>

        <VStack align="stretch" bg="white" my="4" gap="3">
          <HStack py="2">
            <InputGroup flexBasis="45%">
              <InputLeftElement pointerEvents="none">
                <SearchIcon color="gray.300" />
              </InputLeftElement>
              <Input
                placeholder="Search"
                onChange={e => setSearchTerm(e.target.value)}
              />
            </InputGroup>

            <Button
              leftIcon={<FiltersIcon />}
              color="custom_blue.1"
              fontWeight="semibold"
              fontSize="sm"
              boxShadow="0 1px 2px 0 rgba(16, 24, 40, 0.05)"
              border="1px"
              borderColor="custom_blue.3"
              bg="none"
              _hover={{ bg: 'none' }}
              onClick={onOpen}
            >
              Filters
            </Button>
          </HStack>

          {filters.status && (
            <HStack>
              <RoundedBadge
                colorSchema={filters.status}
                withCloseButton
                onClose={handleRemoveFilter}
              >
                {filters.status}
              </RoundedBadge>
            </HStack>
          )}

          {isLoading || isFetching ? (
            <CenteredSpinner />
          ) : (
            <Box>
              <TableContainer
                h="full"
                display="flex"
                flexDirection="column"
                justifyContent="space-between"
                minH="70vh"
              >
                <Table variant="simple">
                  <Thead bg="custom_gray.2">
                    <Tr>
                      <TableHeader title="Users" />
                      <TableHeader title="Status" />
                      <TableHeader title="Email" />
                      <TableHeader title="Phone Number" />
                      <TableHeader title="Actions" />
                    </Tr>
                  </Thead>
                  <Tbody>
                    {data?.data.content.map(
                      ({
                        countryCode,
                        id,
                        status,
                        firstName,
                        lastName,
                        email,
                        phoneNumber,
                        profilePictureThumbnailURL,
                        role,
                      }) => (
                        <Tr key={id}>
                          <Td border="none">
                            <HStack>
                              <UserData
                                roleName={role.name}
                                firstName={firstName}
                                lastName={lastName}
                                pictureUrl={profilePictureThumbnailURL}
                              />
                            </HStack>
                          </Td>
                          <Td border="none">
                            <RoundedBadge colorSchema={status}>
                              {status}
                            </RoundedBadge>
                          </Td>
                          <Td border="none">{email}</Td>

                          <Td letterSpacing="tighter" border="none">
                            + {countryCode} {phoneNumber}
                          </Td>

                          <Td color="custom_blue.1" fontSize="xs" border="none">
                            <Menu>
                              <MenuButton
                                as={IconButton}
                                icon={<DotsIcon />}
                                aria-label="Actions role"
                                bg="transparent"
                              />
                              <MenuList>
                                {/* TODO: reuse after implementation */}
                                {/* <MenuItem>Update Member</MenuItem> */}
                                <MenuItem
                                  onClick={() => {
                                    // TODO: Add delete handler
                                    unAssignTeamMemberToPhase({
                                      teamMemberId: String(id),
                                      phaseId,
                                      projectId: String(projectId),
                                    });
                                  }}
                                >
                                  Remove Member
                                </MenuItem>
                              </MenuList>
                            </Menu>
                          </Td>
                        </Tr>
                      )
                    )}
                  </Tbody>
                </Table>

                <Paginator
                  pageCount={Number(data?.data.totalPages)}
                  onPageChange={page => setPage(page)}
                />
              </TableContainer>
            </Box>
          )}
        </VStack>
      </Box>
    </Box>
  );
};
